import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, X, Smartphone, Palette, ScanLine, BellRing, BarChart3 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { BrandLogo } from '../components/brand/BrandLogo';
import { CARDS, STEPS, PLANS, CSS, PhoneWallet, SmallPhone, FAQItem } from './LandingPage';
import { safeCapture } from '../lib/analytics';
import { getAttribution } from '../lib/attribution';

/*
 * /essai — landing page for paid ads (Meta, Google).
 *
 * A visitor from an ad usually doesn't know what a "Wallet card" is. So the
 * page explains before it asks: outcome → proof → what it is → how it works
 * → try it on your own phone → paper vs Wallet → price → objections.
 * One action everywhere: start the free trial. No navigation that leaks
 * the visitor elsewhere. Every promise matches what the product does today
 * (promos: iPhone, 2 per day max).
 *
 * Optional live demo: set REACT_APP_DEMO_JOIN_URL to the join link of a demo
 * card (e.g. https://getloyalpass.fr/join/<id>); the section hides otherwise.
 */

const DEMO_URL = process.env.REACT_APP_DEMO_JOIN_URL;

/* Lock screen date = today, so the mock never looks stale. */
const TODAY = (() => {
  try { return new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }); } catch { return ''; }
})();

const TRUST = [CARDS.kepasta, CARDS.crepmoi, CARDS.epices, CARDS.shaneliss];

const COMPARE = [
  ['Se perd, s’oublie au fond d’un sac', 'Toujours dans le téléphone, à côté de la carte bancaire'],
  ['Un tampon, ça s’imite', 'Seul votre compte peut tamponner'],
  ['À réimprimer sans arrêt', 'Rien à imprimer'],
  ['Vous ne savez pas qui revient', 'Vous voyez qui revient, et qui est proche du cadeau'],
  ['Impossible de prévenir vos clients', 'Un message sur l’écran verrouillé de leur iPhone'],
];

const FEATURES = [
  { icon: Palette, title: 'À vos couleurs', text: 'Votre logo, vos couleurs, une photo de votre commerce, vos tampons ou vos points, votre récompense.' },
  { icon: ScanLine, title: 'Un scan avec votre téléphone', text: 'Pas de matériel à acheter. L’appareil photo du téléphone de la caisse suffit, pour toute l’équipe.' },
  { icon: BellRing, title: 'Vos offres sur l’écran verrouillé', text: '« Ce midi, le dessert est offert. » Jusqu’à 2 messages par jour, reçus sur iPhone, sans payer de SMS.' },
  { icon: BarChart3, title: 'Vous voyez ce qui marche', text: 'Clients revenus, passages, nouveaux clients, récompenses données : sur les 30 derniers jours.' },
];

const FAQS = [
  { q: 'C’est quoi, Apple Wallet et Google Wallet ?', a: 'L’application déjà installée sur les iPhone (Apple Wallet) et sur la plupart des Android (Google Wallet). Vos clients y rangent leurs billets de train, cartes d’embarquement ou cartes bancaires. Votre carte de fidélité s’y range de la même façon.' },
  { q: 'Mes clients doivent-ils télécharger une application ?', a: 'Non. Ils scannent le QR de votre boutique avec l’appareil photo, laissent un prénom et un email, et appuient sur « Ajouter ». C’est tout.' },
  { q: 'Et mes clients pas à l’aise avec le téléphone ?', a: 'S’ils savent prendre une photo, ils savent scanner un QR. Ça prend une trentaine de secondes, et vous pouvez les guider la première fois au comptoir.' },
  { q: 'Faut-il acheter un appareil pour scanner ?', a: 'Non. Vous scannez avec l’appareil photo de votre téléphone. Toute l’équipe peut le faire avec le même compte.' },
  { q: 'J’ai déjà des clients avec une carte papier.', a: 'Ils s’inscrivent en scannant le QR de votre boutique, et vous leur reportez leurs tampons depuis leur fiche client.' },
  { q: 'Combien de temps pour être prêt ?', a: 'Environ 5 minutes pour créer votre carte. Vous imprimez votre QR boutique, et vos clients peuvent s’inscrire le jour même.' },
  { q: 'Y a-t-il un engagement ?', a: 'Non. 14 jours gratuits sans carte bancaire, puis un abonnement mensuel que vous arrêtez quand vous voulez.' },
  { q: 'Et les données de mes clients ?', a: 'Elles vous appartiennent. Elles sont stockées en Europe, conformément au RGPD, et ne sont jamais revendues.' },
];

const track = (event, props) => {
  const acq = getAttribution();
  safeCapture(event, { page: 'essai', source: acq?.source || 'direct', campaign: acq?.campaign, ...props });
};

/* Adds `is-in` once the element scrolls into view (once). */
const useInView = () => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setInView(true); return undefined; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); io.disconnect(); } }, { threshold: 0.35 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, inView];
};

const Cta = ({ position, children = 'Créer ma carte gratuitement', tone = 'brand', className = '' }) => (
  <Link
    to="/register"
    onClick={() => track('ad_cta_clicked', { position })}
    className={`inline-flex items-center justify-center gap-2 h-14 px-7 whitespace-nowrap rounded-2xl text-[17px] font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--brand)] ${
      tone === 'light' ? 'bg-white text-[var(--ink)] hover:bg-white/90' : 'bg-[var(--brand)] hover:bg-[var(--brand-ink)] text-white'
    } ${className}`}
    data-testid={`ad-cta-${position}`}
  >
    {children} <ArrowRight className="w-5 h-5" />
  </Link>
);

const Reassure = ({ light = false }) => (
  <ul className={`flex flex-wrap gap-x-5 gap-y-2 text-[15px] ${light ? 'text-white/75' : 'text-[var(--muted)]'}`}>
    {['14 jours gratuits', 'Sans carte bancaire', 'Prête en 5 minutes'].map((t) => (
      <li key={t} className="flex items-center gap-1.5">
        <Check className={`w-4 h-4 ${light ? 'text-white' : 'text-[var(--ink)]'}`} strokeWidth={2.5} />{t}
      </li>
    ))}
  </ul>
);

/* iPhone lock screen: what the customer actually sees. */
const LockScreen = () => {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} className="relative mx-auto w-[270px] sm:w-[300px]" data-testid="lockscreen">
      <div className="rounded-[50px] bg-[#0B0B0D] p-[10px] shadow-[0_40px_80px_-30px_rgba(15,17,21,.55),inset_0_0_0_1.5px_#2A2B30]">
        <div
          className="relative rounded-[41px] overflow-hidden"
          style={{ aspectRatio: '9 / 17', background: 'radial-gradient(120% 80% at 20% 10%, #3C5BD8 0%, #1C2452 45%, #0C0F1E 100%)' }}
        >
          <div className="absolute top-[10px] left-1/2 -translate-x-1/2 w-[88px] h-[25px] rounded-full bg-black" />
          <div className="text-center text-white pt-[54px]" style={{ fontFamily: '-apple-system, system-ui, sans-serif' }}>
            <p className="text-[15px] font-semibold opacity-90">{TODAY}</p>
            <p className="text-[74px] font-semibold leading-none tracking-tight mt-0.5">9:41</p>
          </div>
          <div className="absolute left-[9px] right-[9px] bottom-[64px] space-y-2" style={{ fontFamily: '-apple-system, system-ui, sans-serif' }}>
            {[
              { icon: <img src="/landing/logo-kepasta.webp" alt="" className="w-[34px] h-[34px] rounded-[8px] bg-white shrink-0" />, app: 'Kepasta Fidélité', when: '2 min', text: 'Plus que 4 Tampons pour Un Plat Offert' },
              { icon: <span className="w-[34px] h-[34px] rounded-[8px] bg-[#F2B544] shrink-0 grid place-items-center text-[18px]" aria-hidden="true">🥐</span>, app: 'Votre boutique', when: 'maintenant', text: 'Ce midi, le dessert est offert avec votre formule.' },
            ].map((n, i) => (
              <div
                key={n.app}
                className="rounded-[18px] bg-white/80 backdrop-blur-xl px-3 py-2.5 flex gap-2.5 text-[#111] transition-all duration-700 ease-out"
                style={{ transform: inView ? 'none' : 'translateY(24px)', opacity: inView ? 1 : 0, transitionDelay: `${0.2 + i * 0.45}s` }}
              >
                {n.icon}
                <div className="min-w-0 flex-1">
                  <p className="flex justify-between text-[13px] font-semibold leading-tight">
                    <span className="truncate">{n.app}</span>
                    <span className="text-[12px] font-normal text-[#5E5E63] shrink-0 ml-2">{n.when}</span>
                  </p>
                  <p className="text-[13px] leading-snug mt-0.5">{n.text}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="absolute bottom-[8px] left-1/2 -translate-x-1/2 w-[120px] h-[5px] rounded-full bg-white/80" />
        </div>
      </div>
    </div>
  );
};

const AdLandingPage = () => {
  const heroRef = useRef(null);
  const [showSticky, setShowSticky] = useState(false);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Carte de fidélité Apple Wallet & Google Wallet · LoyalPass';
    // Ad-only page: keep it out of search results (the home page is the one to index).
    const robots = document.createElement('meta');
    robots.name = 'robots';
    robots.content = 'noindex, follow';
    document.head.appendChild(robots);
    track('ad_landing_viewed');
    return () => { document.title = prevTitle; robots.remove(); };
  }, []);

  useEffect(() => {
    const el = heroRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(([e]) => setShowSticky(!e.isIntersecting), { threshold: 0 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div className="lp min-h-screen bg-white text-[var(--ink)] pb-24 md:pb-0">
      <style>{CSS}</style>

      {/* ═══ HEADER: logo + one action ═══ */}
      <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-md border-b border-[var(--line)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <BrandLogo className="sm:hidden" size="sm" collapse />
          <BrandLogo className="hidden sm:inline-flex" />
          <nav className="flex items-center gap-1 sm:gap-3">
            {/* Existing merchants also land here from an ad or a shared link */}
            <Link
              to="/login"
              onClick={() => track('ad_login_clicked')}
              className="whitespace-nowrap text-[15px] font-medium text-[var(--ink)] px-2.5 py-2"
              data-testid="ad-login"
            >
              Connexion
            </Link>
            <Link
              to="/register"
              onClick={() => track('ad_cta_clicked', { position: 'header' })}
              className="whitespace-nowrap text-[15px] font-semibold text-white bg-[var(--ink)] hover:bg-black rounded-xl px-3.5 sm:px-4 py-2.5"
              data-testid="ad-cta-header"
            >
              Essai gratuit
            </Link>
          </nav>
        </div>
      </header>

      {/* ═══ HERO ═══ */}
      <section ref={heroRef} className="bg-[var(--page)] overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-10 pb-14 lg:py-20 grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-8 items-center">
          <div>
            <p className="text-[15px] font-semibold text-[var(--brand)]">Pour les restaurants, cafés, boulangeries, instituts et commerces de quartier</p>
            <h1 className="lp-display mt-4 text-[44px] leading-[1.02] sm:text-[62px] lg:text-[68px] font-semibold" data-testid="ad-hero-title">
              Vos clients reviennent.
            </h1>
            <p className="mt-5 text-[18px] sm:text-[20px] leading-relaxed text-[var(--muted)] max-w-[36ch]">
              Votre carte de fidélité dans leur téléphone, à vos couleurs. Fini la carte papier perdue : ils sont tamponnés à chaque passage et pensent à revenir.
            </p>
            <div className="mt-8"><Cta position="hero" /></div>
            <div className="mt-6"><Reassure /></div>
          </div>
          <PhoneWallet />
        </div>
      </section>

      {/* ═══ ILS L'UTILISENT ═══ */}
      <section className="border-b border-[var(--line)]" data-testid="ad-trust">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
          <p className="text-[15px] text-[var(--muted)]">
            <span className="font-semibold text-[var(--ink)]">84 %</span> des clients qui scannent le QR en boutique ajoutent la carte à leur téléphone. Mesuré sur 90 jours chez :
          </p>
          <ul className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
            {TRUST.map((c) => (
              <li key={c.src} className="flex items-center gap-3 min-w-0">
                <img src={c.src} alt="" width="720" height="532" loading="lazy" className="w-[64px] sm:w-[76px] rounded-[8px] shadow-[0_6px_14px_-8px_rgba(15,17,21,.5)] shrink-0" />
                <span className="min-w-0">
                  <span className="block text-[15px] font-semibold leading-snug">{c.name}</span>
                  <span className="block text-[13px] text-[var(--muted)] leading-snug">{c.kind}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ═══ C'EST QUOI ═══ */}
      <section className="py-16 sm:py-24" data-testid="ad-explainer">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <h2 className="lp-display text-[34px] sm:text-[46px] font-semibold leading-[1.08] max-w-[16ch]">C’est quoi, une carte Wallet ?</h2>
            <p className="mt-5 text-[17px] sm:text-[18px] leading-relaxed text-[var(--muted)] max-w-[44ch]">
              Wallet, c’est l’application déjà installée sur les iPhone et la plupart des Android. Vos clients y rangent leurs billets de train et leurs cartes d’embarquement. Votre carte de fidélité s’y range de la même façon, <strong className="text-[var(--ink)] font-semibold">sans application à télécharger</strong>.
            </p>
            <ul className="mt-8 space-y-5">
              {[
                { icon: Smartphone, t: 'Toujours sur eux', d: 'Elle est dans le téléphone, à côté de la carte bancaire. Impossible de l’oublier.' },
                { icon: BellRing, t: 'Elle leur parle', d: 'À chaque tampon, la carte se met à jour, et sur iPhone ils reçoivent une notification. Vous pouvez aussi leur envoyer vos offres.' },
              ].map(({ icon: Icon, t, d }) => (
                <li key={t} className="flex gap-4">
                  <span className="w-11 h-11 rounded-xl bg-[var(--page)] grid place-items-center shrink-0"><Icon className="w-5 h-5" /></span>
                  <span>
                    <span className="block text-[18px] font-semibold">{t}</span>
                    <span className="block text-[16px] text-[var(--muted)] mt-1 leading-relaxed">{d}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <LockScreen />
        </div>
      </section>

      {/* ═══ COMMENT ÇA MARCHE ═══ */}
      <section className="bg-[var(--page)] py-16 sm:py-24" id="comment-ca-marche">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="lp-display text-[34px] sm:text-[46px] font-semibold leading-tight max-w-[18ch]">Pour votre client, c’est 30 secondes.</h2>
          <ol className="mt-10 sm:mt-14 flex md:grid md:grid-cols-3 gap-8 overflow-x-auto lp-snap -mx-4 px-4 md:mx-0 md:px-0 pb-2">
            {STEPS.map((s, i) => (
              <li key={s.title} className="shrink-0 w-[78%] sm:w-[56%] md:w-auto">
                <SmallPhone src={s.img} alt={s.title} />
                <p className="mt-6 text-[14px] font-semibold text-[var(--brand)] tabular-nums">Étape {i + 1}</p>
                <h3 className="text-[20px] font-semibold mt-1 leading-snug">{s.title}</h3>
                <p className="text-[16px] text-[var(--muted)] mt-2 leading-relaxed">{s.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ═══ ESSAYEZ ═══ */}
      {DEMO_URL && (
        <section className="py-16 sm:py-20" data-testid="ad-demo">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="rounded-[28px] bg-[var(--ink)] text-white p-8 sm:p-12 grid md:grid-cols-[1fr_auto] gap-10 items-center">
              <div>
                <h2 className="lp-display text-[32px] sm:text-[42px] font-semibold leading-tight">Essayez-la sur votre téléphone.</h2>
                <p className="mt-4 text-[17px] text-white/75 max-w-[44ch] leading-relaxed">
                  Ajoutez une carte de démonstration à votre Wallet, comme le ferait votre client. 10 secondes, sans créer de compte.
                </p>
                <a
                  href={DEMO_URL}
                  onClick={() => track('ad_demo_clicked')}
                  className="md:hidden mt-7 inline-flex items-center gap-2 h-14 px-7 rounded-2xl bg-white text-[var(--ink)] text-[17px] font-semibold"
                  data-testid="ad-demo-btn"
                >
                  Ajouter la carte démo <ArrowRight className="w-5 h-5" />
                </a>
              </div>
              <figure className="hidden md:block text-center">
                <div className="bg-white rounded-[20px] p-4 inline-block"><QRCodeSVG value={DEMO_URL} size={168} level="M" /></div>
                <figcaption className="mt-3 text-[14px] text-white/70">Scannez avec l’appareil photo</figcaption>
              </figure>
            </div>
          </div>
        </section>
      )}

      {/* ═══ PAPIER vs WALLET ═══ */}
      <section className="py-16 sm:py-24" data-testid="ad-compare">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="lp-display text-[34px] sm:text-[46px] font-semibold leading-tight max-w-[18ch]">La carte papier vous coûte des clients.</h2>
          <div className="mt-10 max-w-4xl rounded-[24px] border border-[var(--line)] overflow-hidden">
            <div className="grid grid-cols-2 bg-[var(--page)] text-[14px] font-semibold">
              <p className="px-4 sm:px-6 py-3 text-[var(--muted)]">Carte papier</p>
              <p className="px-4 sm:px-6 py-3 flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[var(--brand)]" />LoyalPass</p>
            </div>
            {COMPARE.map(([paper, wallet]) => (
              <div key={paper} className="grid grid-cols-2 border-t border-[var(--line)] text-[15px] sm:text-[16px]">
                <p className="px-4 sm:px-6 py-4 flex gap-2.5 text-[var(--muted)]"><X className="w-4 h-4 mt-1 shrink-0 text-[#C2410C]" strokeWidth={2.5} />{paper}</p>
                <p className="px-4 sm:px-6 py-4 flex gap-2.5 font-medium"><Check className="w-4 h-4 mt-1 shrink-0 text-[#15803D]" strokeWidth={2.5} />{wallet}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CE QUE VOUS AVEZ ═══ */}
      <section className="bg-[var(--page)] py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="lp-display text-[34px] sm:text-[46px] font-semibold leading-tight max-w-[20ch]">Tout ce qu’il faut, rien de compliqué.</h2>
          <div className="mt-10 grid sm:grid-cols-2 gap-5">
            {FEATURES.map(({ icon: Icon, title, text }) => (
              <div key={title} className="bg-white rounded-[22px] p-6 sm:p-7">
                <span className="w-11 h-11 rounded-xl bg-[var(--brand)] text-white grid place-items-center"><Icon className="w-5 h-5" /></span>
                <h3 className="text-[20px] font-semibold mt-5">{title}</h3>
                <p className="text-[16px] text-[var(--muted)] mt-2 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ TÉMOIGNAGE ═══ */}
      <section className="py-16 sm:py-24">
        <figure className="max-w-4xl mx-auto px-4 sm:px-6">
          <blockquote className="lp-display text-[28px] sm:text-[40px] font-semibold leading-[1.15]">
            « LoyalPass nous a permis de fidéliser nos clientes facilement, sans qu’elles aient à installer une nouvelle app. »
          </blockquote>
          <figcaption className="mt-6 text-[16px] text-[var(--muted)]">Mestora, Modestie Luxueuse</figcaption>
        </figure>
      </section>

      {/* ═══ PRIX ═══ */}
      <section className="bg-[var(--page)] py-16 sm:py-24" id="tarifs">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="lp-display text-[34px] sm:text-[46px] font-semibold leading-tight">Un prix simple.</h2>
          <p className="text-[17px] text-[var(--muted)] mt-3">14 jours gratuits, sans carte bancaire. Ensuite, sans engagement.</p>
          <div className="mt-10 grid md:grid-cols-2 gap-6 max-w-4xl">
            {PLANS.map((plan) => (
              <div key={plan.name} className={`rounded-[24px] p-7 sm:p-8 flex flex-col ${plan.popular ? 'bg-[var(--ink)] text-white' : 'bg-white'}`}>
                <h3 className="text-[22px] font-semibold">{plan.name}</h3>
                <p className="mt-3 flex items-baseline gap-1.5">
                  <span className="lp-display text-[48px] font-semibold leading-none tabular-nums">{plan.price}&nbsp;€</span>
                  <span className={plan.popular ? 'text-white/70' : 'text-[var(--muted)]'}>/mois</span>
                </p>
                <ul className="mt-6 space-y-2.5 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className={`flex items-start gap-2.5 text-[15px] ${plan.popular ? 'text-white/90' : ''}`}>
                      <Check className="w-4 h-4 mt-1 shrink-0" strokeWidth={2.5} />{f}
                    </li>
                  ))}
                </ul>
                <Cta position={`plan-${plan.name.toLowerCase()}`} tone={plan.popular ? 'light' : 'brand'} className="mt-8 w-full">
                  Commencer l’essai
                </Cta>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ QUESTIONS ═══ */}
      <section className="py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid md:grid-cols-[1fr_1.6fr] gap-8 md:gap-16">
          <h2 className="lp-display text-[34px] sm:text-[46px] font-semibold leading-tight">Vos questions.</h2>
          <div className="rounded-[24px] border border-[var(--line)] px-6 sm:px-8" data-testid="ad-faq">
            {FAQS.map((f) => <FAQItem key={f.q} q={f.q} a={f.a} />)}
          </div>
        </div>
      </section>

      {/* ═══ DERNIER APPEL ═══ */}
      <section className="px-4 sm:px-6 pb-16 sm:pb-24">
        <div className="max-w-6xl mx-auto rounded-[32px] bg-[var(--brand)] text-white text-center px-6 py-14 sm:py-20">
          <h2 className="lp-display text-[36px] sm:text-[54px] font-semibold leading-[1.04] max-w-[18ch] mx-auto">Votre carte peut être prête aujourd’hui.</h2>
          <p className="text-[17px] text-white/80 mt-4">Créez-la en 5 minutes. Vos clients s’inscrivent dès demain.</p>
          <div className="mt-8 flex justify-center"><Cta position="final" tone="light" /></div>
          <div className="mt-6 flex justify-center"><Reassure light /></div>
        </div>
      </section>

      <footer className="border-t border-[var(--line)] py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-[14px] text-[var(--muted)]">
          <BrandLogo size="sm" />
          <nav className="flex flex-wrap justify-center gap-x-5 gap-y-2">
            <Link to="/mentions-legales" className="hover:text-[var(--ink)]">Mentions légales</Link>
            <Link to="/confidentialite" className="hover:text-[var(--ink)]">Confidentialité</Link>
            <Link to="/cgv" className="hover:text-[var(--ink)]">CGV</Link>
            <a href="mailto:contact@getloyalpass.fr" className="hover:text-[var(--ink)]">contact@getloyalpass.fr</a>
          </nav>
        </div>
      </footer>

      {/* ═══ BOUTON FIXE (mobile, après le haut de page) ═══ */}
      <div
        className={`md:hidden fixed inset-x-0 bottom-0 z-50 px-4 pt-3 bg-white/95 backdrop-blur border-t border-[var(--line)] transition-transform duration-300 ${showSticky ? 'translate-y-0' : 'translate-y-full'}`}
        style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}
        aria-hidden={!showSticky}
        data-testid="ad-sticky"
      >
        <Link
          to="/register"
          tabIndex={showSticky ? 0 : -1}
          onClick={() => track('ad_cta_clicked', { position: 'sticky' })}
          className="flex items-center justify-center gap-2 h-[52px] rounded-2xl bg-[var(--brand)] text-white text-[17px] font-semibold"
        >
          Essai gratuit 14 jours <ArrowRight className="w-5 h-5" />
        </Link>
      </div>
    </div>
  );
};

export default AdLandingPage;
